import type { ReactNode } from "react";

import { ActionBar } from "./ActionBar";
import { Button } from "./Button";
import { FormCard } from "./FormCard";

export interface WizardStep {
  title: string;
  content: ReactNode;
}

export interface WizardProps {
  steps: WizardStep[];
  active: number;
  onBack: () => void;
  onNext: () => void;
  onFinish: () => void;
  /** Disables Next/Finish (e.g. current step invalid). */
  proceedDisabled?: boolean;
  finishLabel?: string;
  busy?: boolean;
  /**
   * Click-to-edit: when provided, already-visited steps (index ≤ active) become clickable and jump
   * straight to that step. Omit for a plain linear stepper (back/next only).
   */
  onStepClick?: (index: number) => void;
}

/**
 * jve-7: the reusable multi-step wizard chrome — the bespoke-flow UI pattern (intake/surrender,
 * release/adoption). Renders a stepper header, the active step's body, and a Back / Next / Finish
 * action bar. Step state + validation live in the host; this is the layout + navigation vocabulary.
 */
export function Wizard({
  steps,
  active,
  onBack,
  onNext,
  onFinish,
  proceedDisabled = false,
  finishLabel = "Voltooien",
  busy = false,
  onStepClick,
}: WizardProps) {
  const isLast = active === steps.length - 1;

  return (
    <FormCard>
      <ol className="fui-stepper">
        {steps.map((step, i) => {
          const clickable = onStepClick != null && i <= active && i !== active;
          return (
            <li
              key={i}
              className={
                "fui-step" +
                (i === active ? " is-active" : "") +
                (i < active ? " is-done" : "") +
                (clickable ? " is-clickable" : "")
              }
              aria-current={i === active ? "step" : undefined}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              style={clickable ? { cursor: "pointer" } : undefined}
              onClick={clickable ? () => onStepClick(i) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onStepClick(i);
                      }
                    }
                  : undefined
              }
            >
              <span className="fui-step__num">{i + 1}</span>
              <span className="fui-step__title">{step.title}</span>
            </li>
          );
        })}
      </ol>

      <div className="fui-wizard__body">{steps[active]?.content}</div>

      <ActionBar>
        {active > 0 && (
          <Button variant="secondary" onClick={onBack} disabled={busy}>
            Terug
          </Button>
        )}
        {isLast ? (
          <Button onClick={onFinish} disabled={proceedDisabled || busy}>
            {finishLabel}
          </Button>
        ) : (
          <Button onClick={onNext} disabled={proceedDisabled || busy}>
            Volgende
          </Button>
        )}
      </ActionBar>
    </FormCard>
  );
}
